import { useMutation, useQuery } from "@tanstack/react-query";

import { queryClient } from "App";
import { revenueOpportunityKeys } from "components/RevenueOpportunitiesTableV3/queryKeys";
import AttachmentService from "services/Attachment";
import RevenueOpportunityService from "services/RevenueOpportunities";

// The table filter options are distinct values read off the opportunity rows,
// so a new order number or assignee has to reach the dropdowns too.
const OPTION_KEYS = [
  ["purchase-orders-options"],
  ["sales-orders-options"],
  ["invoices-options"],
  ["assignee-options"],
];

const invalidateRevenueOpportunity = id =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: revenueOpportunityKeys.lists() }),
    ...OPTION_KEYS.map(queryKey => queryClient.invalidateQueries({ queryKey })),
    id != null &&
      queryClient.invalidateQueries({
        queryKey: revenueOpportunityKeys.detail(id),
      }),
  ]);

const removeAttachment = async (revenueOpportunityId, attachmentId) => {
  await RevenueOpportunityService.removeRevenueOpportunityAttachment(
    revenueOpportunityId,
    attachmentId
  );
  await AttachmentService.delete(attachmentId);
};

export const useRevenueOpportunity = id =>
  useQuery({
    queryKey: revenueOpportunityKeys.detail(id),
    queryFn: () => RevenueOpportunityService.getRevenueOpportunityById(id),
    enabled: !!id,
    retry: false,
  });

export const useRevenueOpportunityMutations = () => {
  const create = useMutation({
    mutationFn: data =>
      RevenueOpportunityService.createRevenueOpportunity(data),
    onSuccess: saved => invalidateRevenueOpportunity(saved?.id),
  });

  // Removed attachments are deleted only after the update succeeds, so a
  // failed save leaves the record exactly as it was.
  const update = useMutation({
    mutationFn: async ({ id, data, removedAttachmentIds = [] }) => {
      const saved = await RevenueOpportunityService.editRevenueOpportunity(
        id,
        data
      );
      await Promise.all(
        removedAttachmentIds.map(attachmentId =>
          removeAttachment(id, attachmentId)
        )
      );
      return saved;
    },
    onSuccess: (_, { id }) => invalidateRevenueOpportunity(id),
  });

  const setLocked = useMutation({
    mutationFn: ({ id, locked }) =>
      locked
        ? RevenueOpportunityService.lockRevenueOpportunity(id)
        : RevenueOpportunityService.unlockRevenueOpportunity(id),
    onSuccess: (_, { id }) => invalidateRevenueOpportunity(id),
  });

  return { create, update, setLocked };
};
